import { useEffect, useState } from 'react';

import clsx from 'clsx';

import { ActionSendButton } from './ActionSendButton';
import { BaseActionProps } from '../../../types/Action/Action';
import { IDataPointOption } from '../../../types/DataPoint/DataPointOption';
import { useFreeTierNotification } from '../../../hooks/useFreeTierNotification';

interface ToggleActionProps extends BaseActionProps {
  currentValue: number | boolean;
  leftOption?: IDataPointOption;
  rightOption?: IDataPointOption;
}

/**
 * A value input with simple up/down pickers, takes a variety of props to make it suitable for different situations
 *
 * @component
 * @param {number} currentValue required - gives a starting point for the control.
 * @param {IDataPointOption} leftOption optional - option to send when left side is selected, defaults to "1"
 * @param {IDataPointOption} rightOption optional - option to send when right side is selected, defaults to "0"
 * @param {function} onApply required - function to run when the user clicks apply.
 * @param {string} actionId required - ID of this action.
 * @param {boolean} pendingAction required - if true disables send button
 */

export function ToggleAction({
  currentValue,
  leftOption,
  rightOption,
  onApply,
  actionId,
  pendingAction,
  disabled = false,
  paidTier = false,
}: ToggleActionProps) {
  const showFreeTierMessage = useFreeTierNotification();
  const [blockUpdate, setBlockUpdate] = useState(false);
  const [value, setValue] = useState(currentValue);

  const handleApply = () => {
    onApply(value.toString(), actionId);
    setBlockUpdate(false);
  };

  const handleToggle = (value: number) => {
    setValue(value);
    setBlockUpdate(true);
  };

  useEffect(() => {
    if (!blockUpdate) {
      setValue(currentValue);
    }
  }, [blockUpdate, currentValue]);

  const isCurrentValue = value === currentValue;

  return (
    <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
      <div className="md:col-span-6">
        <div className="w-full h-14 flex flex-row border bg-primary border-primary rounded-sm">
          <div
            onClick={
              Boolean(
                value === leftOption?.value ||
                pendingAction !== 'idle' ||
                disabled
              )
                ? () => {}
                : !paidTier
                  ? () => showFreeTierMessage('action')
                  : () => handleToggle(leftOption?.value!)
            }
            className={clsx(
              'flex pt-1 items-center justify-center w-1/2 text-4xl font-medium select-none',
              pendingAction !== 'idle' || disabled || !paidTier
                ? 'bg-gray-200 hover:curser-default hover:bg-gray-200 text-gray-400'
                : value === leftOption?.value
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-400 hover:cursor-pointer hover:bg-white/10 hover:text-white'
            )}
          >
            {leftOption?.label}
          </div>
          <div
            onClick={
              Boolean(
                value === rightOption?.value ||
                pendingAction !== 'idle' ||
                disabled
              )
                ? () => {}
                : !paidTier
                  ? () => showFreeTierMessage('action')
                  : () => handleToggle(rightOption?.value!)
            }
            className={clsx(
              'flex pt-1 items-center justify-center w-1/2 text-4xl font-medium select-none',
              pendingAction !== 'idle' || disabled || !paidTier
                ? 'bg-gray-200 hover:curser-default hover:bg-gray-200 text-gray-400'
                : value === rightOption?.value
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-400 hover:cursor-pointer hover:bg-white/10 hover:text-white'
            )}
          >
            {rightOption?.label}
          </div>
        </div>
      </div>
      <div className="md:col-span-2">
        <ActionSendButton
          onClick={handleApply}
          pendingAction={pendingAction}
          disabled={
            pendingAction === 'timedOut' ? false : isCurrentValue || disabled
          }
          paidTier={paidTier}
        />
      </div>
    </div>
  );
}
